/* eslint-disable ember/no-side-effects */
import Controller from '@ember/controller';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';
import dayjs from 'dayjs';
import { task } from 'ember-concurrency';
import { getCategoricalColors } from 'frontend-utils/utils/css-variable';
import { getHostDomain } from 'frontend-utils/utils/domain-checks';
import ENV from 'lumen/config/environment';
import {
  MEALTYPE_ICONS,
  MEALTYPE_TRANSLATION_KEYS,
} from 'lumen/utils/constants/general-constants';
import {
  OCCUPANCY_GUESTS,
  PMS_SETTINGS_OFFSET_SOURCES,
  PMS_SETTINGS_OFFSET_TYPES,
  SUPPORTED_MEALTYPES,
} from 'lumen/utils/constants/pricing-assistant-constants';
import safeMultiply from 'lumen/utils/numeric/safe-multiply';
import {
  carryOverIncludedFlags,
  collectIncludedFlags,
} from 'lumen/utils/pricing-assistant/pms-settings-carry-over-included-flags';

export default class HotelSettingsPmsIntegrationController extends Controller {
  @service access;
  @service('hotel-and-market') hotelAndMarketService;
  @service intl;
  @service localStorage;
  @service pricingAssistantPmsAccess;
  @service pricingAssistantPmsSs;
  @service toast;
  @service usageAnalytics;

  @tracked canShowPmsRoomUnselectionBanner = true;
  // More naive change detection. Default is false, and is set to true when the user changes any setting.
  @tracked hasUserChangedSettingsValue = false;
  // Make a local copy of the current PMS Settings. This local copy will be used to render the state of the page and to store the user's changes before saving.
  @tracked localSettingsCopy = JSON.parse(
    JSON.stringify(this.caxxxPmsSettings),
  );

  @tracked showPmsSettingsValueInvalidAfterSimulationBanner = false;

  CHANNEL_ROOM_COLORS = getCategoricalColors();
  localStorageKeyForPmsRoomUnselectionBanner;
  // Latest include flag per room for this editing session, including unsaved changes.
  pmsRoomIncludedFlags = {};

  // Note: this is a task so that it can deal with cancellation better
  // -> this.model.launchPmsSettingsSimulateRequestTask is a restartable task and can be triggered multiple times in a short window
  // More info: https://ember-concurrency.com/docs/task-cancelation-help
  launchPmsSettingsSimulateRequestTask = task(
    { restartable: true },
    async () => {
      const result =
        await this.model.launchPmsSettingsSimulateRequestTask.perform(
          this.localSettingsCopy,
        );
      if (result.isSuccess) {
        this.showPmsSettingsValueInvalidAfterSimulationBanner =
          !result.arePmsSettingsValid;
      }
    },
  );

  get arePmsPrefillOptionsLoading() {
    return this.model.pmsPrefillOptionsTask.isRunning;
  }

  get arePmsSettingsUpdating() {
    return this.model.saveAndUpdatePmsSettingsTask.isRunning;
  }

  // Preserve reference for isEqual check in PowerSelect code
  @cached
  get baseRateOptions() {
    if (this.arePmsPrefillOptionsLoading) {
      // We use the default value to fill the dropdown while the options endpoint loads to avoid showing an empty dropdown
      return [
        {
          id: this.localSettingsCopy.pms_root_rate_id,
          label: this.localSettingsCopy.pms_root_rate_id,
        },
      ];
    }
    const rates = Object.keys(this.pmsPrefillOptions.pmsRootRateIdMapping);

    return rates.map((rate) => ({
      id: rate,
      label: rate,
    }));
  }

  get caxxxPmsSettings() {
    return this.hotelAndMarketService.hotel.caxxxPmsSettings;
  }

  // We need to get the channel's leading room from a prefill mapping object using the leading room
  get channelLeadingRoom() {
    return this.localSettingsCopy.pms_id_to_booking_room_name_mapping[
      this.localSettingsCopy.pms_leading_room_id
    ];
  }

  // Preserve reference for isEqual check in PowerSelect code
  @cached
  get channelLeadingRoomOptions() {
    let options = [];

    // Show mapped channel leading room as options until prefill options are loaded
    if (this.arePmsPrefillOptionsLoading) {
      options.push({
        id: this.channelLeadingRoom,
        label: this.channelLeadingRoom,
      });
    } else {
      options = this.channelRoomNameOptions;
    }

    return options;
  }

  // Used as content for all channel room dropdowns on the page
  // - Leading room & channel room & the room types offsets section rows
  get channelRoomNameOptions() {
    if (!this.arePmsPrefillOptionsLoading) {
      return this.sortedPmsPrefillOptionsChannelRoomsNames.map(
        (bookingRoomName) => ({
          id: bookingRoomName,
          label: bookingRoomName,
        }),
      );
    }

    return [];
  }

  // Each channel room is assigned a color shown in the UI
  get channelRoomNameToColorMapping() {
    const colorMap = {};

    // Since we already show the mapped channel rooms in the UI even if the possible channel room options are still being requested, we can assign color to these already shown mapped channel rooms first
    // We sort the mapped channel rooms to ensure that the same channel room is mapped to the same color
    const mappedChannelRoomNames = Object.values(
      this.caxxxPmsSettings.pms_id_to_booking_room_name_mapping,
    ).sort();

    // Different pms rooms can be mapped to same channel room. Therefore, we need to remove duplicates from the mapped channel rooms array before assigning color
    let channelRoomNames = [...new Set([...mappedChannelRoomNames])];

    // Once all the channel rooms options are fetched, we now assign colors to the remaining/unmapped channel rooms
    if (!this.arePmsPrefillOptionsLoading) {
      // BE doesn't guarantee the order of the channel rooms in the prefill options response. Therefore, we sort the channel rooms before assigning color to keep the same channel room mapped to the same color.
      const unmappedChannelRoomNames =
        this.sortedPmsPrefillOptionsChannelRoomsNames.filter(
          (channelRoom) => !mappedChannelRoomNames.includes(channelRoom),
        );

      // Channel rooms are mapped color based on the index at which they appear in the channelRoomNames array
      // In order to ensure that the same channel room is mapped to same color, the unmapped channel rooms are pushed at the end of the array
      channelRoomNames = [...channelRoomNames, ...unmappedChannelRoomNames];
    }

    // Assign color to each channel room
    channelRoomNames.forEach((channelRoomName, index) => {
      colorMap[channelRoomName] = this.CHANNEL_ROOM_COLORS[index];
    });

    return colorMap;
  }

  get customerCareAccessExpireDateTimestamp() {
    if (this.localSettingsCopy.customer_care_access_expire_date === null) {
      return null;
    }

    const expireDateTimestamp = dayjs(
      this.localSettingsCopy.customer_care_access_expire_date,
    );

    return {
      date: expireDateTimestamp.format('LL'),
      time: expireDateTimestamp.format('h:mm A'),
    };
  }

  get customerCareAccessToggleMessage() {
    if (this.hasUserChangedSettingsValue) {
      return this.intl.t(
        'pricing_assistant.customer_care_access.unsaved_changes.message',
      );
    }

    if (this.paAutopilotSettings?.enabled) {
      return this.intl.t(
        'pricing_assistant.customer_care_access.autopilot_enabled.message',
      );
    }

    if (
      this.localSettingsCopy.customer_care_access_given &&
      this.customerCareAccessExpireDateTimestamp
    ) {
      return this.intl.t(
        'pricing_assistant.customer_care_access.enabled.message',
        {
          date: this.customerCareAccessExpireDateTimestamp.date,
          time: this.customerCareAccessExpireDateTimestamp.time,
        },
      );
    }

    return this.intl.t(
      'pricing_assistant.customer_care_access.default.message',
    );
  }

  get editOffsetUrl() {
    const { id } = this.hotelAndMarketService.hotel;
    return `${getHostDomain({ frontendGateway: ENV.frontendGateway })}/apigateway/v1/app/channel-management/channelmanager/outgoing?subscription_id=${id}&page=integrations-pms`;
  }

  // Numeric value bound to the editable channel difference input (shown when the
  // offset is managed in PMS settings). See readOnlyChannelDifferenceValue for the
  // read-only badge label used when the offset is sourced from the channel manager.
  get formattedChannelDifferenceValue() {
    let channelDifferenceValue =
      this.localSettingsCopy.pms_root_rate_offset.value;

    if (
      channelDifferenceValue !== null &&
      this.selectedChannelOffsetType.id === PMS_SETTINGS_OFFSET_TYPES.RELATIVE
    ) {
      channelDifferenceValue = safeMultiply(channelDifferenceValue, 100);
    }

    return channelDifferenceValue;
  }

  get formattedPmsExtraGuestOffsetValue() {
    let pmsExtraGuestOffsetValue =
      this.localSettingsCopy.pms_persons_mapping[OCCUPANCY_GUESTS.EXTRA_ADULT]
        .value;

    if (
      pmsExtraGuestOffsetValue !== null &&
      this.selectedPmsExtraGuestOffsetType.id ===
        PMS_SETTINGS_OFFSET_TYPES.RELATIVE
    ) {
      pmsExtraGuestOffsetValue = safeMultiply(pmsExtraGuestOffsetValue, 100);
    }

    return pmsExtraGuestOffsetValue;
  }

  get formattedPmsGuest() {
    const formattedGuests = {
      [OCCUPANCY_GUESTS.ADULT_1]: {
        iconName: 'user',
        labelValue: this.intl.t('rates.persons_selection', { count: 1 }),
      },
      [OCCUPANCY_GUESTS.ADULT_2]: {
        iconName: 'user-group',
        labelValue: this.intl.t('rates.persons_selection', { count: 2 }),
      },
    };
    return formattedGuests[this.pmsGuest];
  }

  get formattedPmsGuestOffsetValue() {
    let pmsGuestOffsetValue =
      this.localSettingsCopy.pms_persons_mapping[this.pmsGuest].value;

    if (
      pmsGuestOffsetValue !== null &&
      this.selectedPmsGuestOffsetType.id === PMS_SETTINGS_OFFSET_TYPES.RELATIVE
    ) {
      pmsGuestOffsetValue = safeMultiply(pmsGuestOffsetValue, 100);
    }

    return pmsGuestOffsetValue;
  }

  get hasInvalidPmsOccupancyOffsetInputValue() {
    if (!this.pmsOccupancyGuestMapping) {
      return false;
    }
    // pmsOccupancyGuestMapping is an object, that is why we take its values (guests) to an array to be able to check which offsets are available.
    return Object.values(this.pmsOccupancyGuestMapping).some(
      (guest) => guest.value === null,
    );
  }

  get hasInvalidPmsRoomtypeHierarchyOffsetInputValue() {
    // pmsRoomtypeHierarchyLeadingRoomChildren is an object, that is why we take its values (rooms) to an array to be able to check which offsets are available.
    return (
      this.localSettingsCopy.pms_root_rate_offset.value === null ||
      Object.values(this.pmsRoomtypeHierarchyLeadingRoomChildren).some(
        (room) => room.offsets[0].value === null,
      )
    );
  }

  get hasPmsRoomtypeHierarchyLeadingRoomChildren() {
    return Object.keys(this.pmsRoomtypeHierarchyLeadingRoomChildren).length > 0;
  }

  // When the offset is sourced from the channel manager it is read-only here and
  // edited in Channel Manager instead. Absent source (e.g. before the BE field
  // ships) falls back to the editable PMS-settings experience.
  get isChannelOffsetReadOnly() {
    return (
      this.localSettingsCopy.pms_root_rate_offset.source ===
      PMS_SETTINGS_OFFSET_SOURCES.CHANNEL_MANAGER
    );
  }

  get isCustomerCareAccessToggleDisabled() {
    // Note: use optional chaining for paAutopilotSettings as not all hotels have autopilot support
    return (
      this.paAutopilotSettings?.enabled || this.hasUserChangedSettingsValue
    ); // Currently, both CC toggle and settings save use the same endpoint. As a result, CC toggle results in saving the settings (not desirable). Therefore, we disable the toggle while user has unsaved changes.
  }

  get isDiscardButtonDisabled() {
    return (
      this.access.isPricingAssistantPmsSettingsActionOrInputDisabled ||
      !this.hasUserChangedSettingsValue ||
      this.arePmsSettingsUpdating
    );
  }

  get isSaveButtonDisabled() {
    return (
      this.access.isPricingAssistantPmsSettingsActionOrInputDisabled ||
      !this.hasUserChangedSettingsValue ||
      this.arePmsSettingsUpdating ||
      this.hasInvalidPmsOccupancyOffsetInputValue ||
      this.hasInvalidPmsRoomtypeHierarchyOffsetInputValue
    );
  }

  // Note: Offset type options are reused for 2 dropdowns on the page:
  // 1. Booking offset type dropdown
  // 2. Roomtype offset type dropdowns
  // Preserve reference for isEqual check in PowerSelect code
  @cached
  get offsetTypeOptions() {
    return [
      {
        id: PMS_SETTINGS_OFFSET_TYPES.ABSOLUTE,
        label: this.hotelAndMarketService.hotel.currencyCode,
      },
      {
        id: PMS_SETTINGS_OFFSET_TYPES.RELATIVE,
        label: this.intl.t(
          'pricing_assistant.edit_display_modal.label_set_difference.percentage',
        ),
      },
    ];
  }

  get paAutopilotSettings() {
    return this.hotelAndMarketService.hotel.paAutopilotSettings;
  }

  // Preserve reference for isEqual check in PowerSelect code
  @cached
  get pmsBaseOccupancyOptions() {
    if (this.arePmsPrefillOptionsLoading) {
      // We use the default value to fill the dropdown while the options endpoint loads to avoid showing an empty dropdown
      return [
        {
          id: this.localSettingsCopy.pms_persons,
          label: this.intl.t('rates.persons_selection', {
            count: this.localSettingsCopy.pms_persons,
          }),
        },
      ];
    }

    return this.pmsPrefillOptions.pmsPersons.map((person) => ({
      id: person,
      label: this.intl.t('rates.persons_selection', { count: person }),
    }));
  }

  get pmsGuest() {
    return Object.keys(this.pmsOccupancyGuestMapping).find(
      (guest) => guest !== OCCUPANCY_GUESTS.EXTRA_GUEST,
    );
  }

  // Preserve reference for isEqual check in PowerSelect code
  @cached
  get pmsLeadingRoomOptions() {
    // We use the default value to fill the dropdown while the options endpoint loads to avoid showing an empty dropdown
    if (this.arePmsPrefillOptionsLoading) {
      return [
        {
          id: this.localSettingsCopy.pms_leading_room_id,
          label: this.localSettingsCopy.pms_leading_room_id,
        },
      ];
    }

    const pmsLeadingRooms = this.getLeadingRoomsFromHierarchyByBaseRate(
      this.localSettingsCopy.pms_root_rate_id,
    );

    return pmsLeadingRooms.map((room) => ({
      id: room,
      label: room,
    }));
  }

  get pmsMealtypes() {
    return SUPPORTED_MEALTYPES.map((mealtype) => ({
      id: mealtype,
      icon: MEALTYPE_ICONS[mealtype],
      title: this.intl.t(MEALTYPE_TRANSLATION_KEYS[mealtype]),
    }));
  }

  // This is an object with data about the guest and extra guest which offsets needs to be set
  // If the PMS is occupancy based it will have the object
  // If the PMS is NOT occupancy based its value will be null and we will hide the rows to set offsets for the different kinds of guest
  get pmsOccupancyGuestMapping() {
    return this.localSettingsCopy.pms_persons_mapping;
  }

  // The PMS prefill options are requested asynchronously in the route. We need the response to fill in the values of some of the dropdowns on the page
  get pmsPrefillOptions() {
    if (this.arePmsPrefillOptionsLoading) {
      return null;
    }

    return this.model.pmsPrefillOptionsTask.last?.value;
  }

  get pmsRoomtypeHierarchy() {
    // Sort the roomtype hierarchy alphabetically before showing it to the user - BE can't guarantee this order because of dynamic keys in the returned hierarchy object
    const sortedHierarchy = this.sortHierarchy(
      this.localSettingsCopy.pms_roomtype_hierarchy,
    );
    return sortedHierarchy;
  }

  get pmsRoomtypeHierarchyLeadingRoomChildren() {
    return Object.values(this.pmsRoomtypeHierarchy)[0].children;
  }

  get preferredChannelId() {
    return this.hotelAndMarketService.hotel.paRatePreference.channel;
  }

  // Formatted label shown in the read-only channel difference badge (when the
  // offset is sourced from the channel manager). Counterpart to
  // formattedChannelDifferenceValue, which feeds the editable input.
  get readOnlyChannelDifferenceValue() {
    const value = this.localSettingsCopy.pms_root_rate_offset.value;

    if (value === null || value === undefined) {
      return this.intl.t('general.missing');
    }

    if (
      this.selectedChannelOffsetType.id === PMS_SETTINGS_OFFSET_TYPES.RELATIVE
    ) {
      return this.intl.formatNumber(value, {
        style: 'percent',
        signDisplay: 'always',
        maximumFractionDigits: 2,
      });
    }

    return this.hotelAndMarketService.formatRate(value, { includeSign: true });
  }

  // Inline unit shown in the room hierarchy offset inputs: hotel currency for
  // absolute offsets, % for relative ones. Tracks the offset-type dropdown.
  get roomtypeOffsetUnit() {
    return this.selectedRoomtypeOffsetsType?.id ===
      PMS_SETTINGS_OFFSET_TYPES.RELATIVE
      ? '%'
      : this.hotelAndMarketService.hotel.currencySymbol;
  }

  get selectedBaseRate() {
    return this.baseRateOptions.find(
      (option) => option.id === this.localSettingsCopy.pms_root_rate_id,
    );
  }

  get selectedChannelLeadingRoom() {
    return this.channelLeadingRoomOptions.find(
      (option) => option.id === this.channelLeadingRoom,
    );
  }

  get selectedChannelMatchingRate() {
    return this.localSettingsCopy.booking_root_rate_id;
  }

  get selectedChannelOffsetType() {
    return this.offsetTypeOptions.find(
      (option) =>
        option.id === this.localSettingsCopy.pms_root_rate_offset.type,
    );
  }

  get selectedPmsBaseOccupancy() {
    return this.pmsBaseOccupancyOptions.find(
      (option) => option.id === this.localSettingsCopy.pms_persons,
    );
  }

  get selectedPmsExtraGuestOffsetType() {
    return this.offsetTypeOptions.find(
      (option) =>
        option.id ===
        this.localSettingsCopy.pms_persons_mapping[OCCUPANCY_GUESTS.EXTRA_ADULT]
          .type,
    );
  }

  get selectedPmsGuestOffsetType() {
    return this.offsetTypeOptions.find(
      (option) =>
        option.id ===
        this.localSettingsCopy.pms_persons_mapping[this.pmsGuest].type,
    );
  }

  get selectedPmsLeadingRoom() {
    return this.pmsLeadingRoomOptions.find(
      (option) => option.id === this.localSettingsCopy.pms_leading_room_id,
    );
  }

  get selectedPmsMealtype() {
    return this.localSettingsCopy.pms_mealtype;
  }

  get selectedRoomtypeOffsetsType() {
    // Get leading room offset type. All other rooms in the hierarchy should have the same offset type.
    const leadingRoomOffsetType = Object.values(this.pmsRoomtypeHierarchy)[0]
      .offsets[0].type;

    return this.offsetTypeOptions.find(
      (option) => option.id === leadingRoomOffsetType,
    );
  }

  get showPmsRoomUnselectionBanner() {
    // Setting up the local storage key. Can't do it at the time of initialisation because hotelAndMarketService is not available at that time
    this.localStorageKeyForPmsRoomUnselectionBanner = `caxxxIsPmsRoomUnselectionBannerVisible_${this.hotelAndMarketService.hotel?.id}`;

    // Fetch the banner visibility from local storage
    if (
      this.localStorage.getItem(this.localStorageKeyForPmsRoomUnselectionBanner)
    ) {
      this.canShowPmsRoomUnselectionBanner = JSON.parse(
        this.localStorage.getItem(
          this.localStorageKeyForPmsRoomUnselectionBanner,
        ),
      );
    }

    // Check if a room type is unselected only if we can show the banner
    if (this.canShowPmsRoomUnselectionBanner) {
      return Object.values(this.pmsRoomtypeHierarchyLeadingRoomChildren).some(
        (room) => room.included === false,
      );
    }

    return false;
  }

  // BE doesn't ensure a deterministic order of bookingRoomNames, which causes the dropdown to show rooms in different order on each page load
  // Therefore, we sort the rooms alphabetically to ensure consistent order
  get sortedPmsPrefillOptionsChannelRoomsNames() {
    if (
      !this.arePmsPrefillOptionsLoading &&
      this.pmsPrefillOptions?.bookingRoomNames
    ) {
      return this.pmsPrefillOptions.bookingRoomNames.sort();
    }

    return [];
  }

  // Additional logging function to track changed PMS integration settings values in usage tracking
  // Can maybe later be moved to a util once it's been validated more.
  compareSettings(originalSettings, currentActiveSettings) {
    const keysToCompare = [
      'pms_root_rate_id',
      'pms_mealtype',
      'pms_persons',
      'booking_root_rate_id',
      'pms_root_rate_offset',
      'pms_leading_room_id',
      'pms_roomtype_hierarchy',
    ];

    const keyMap = {
      pms_root_rate_id: 'Base rate',
      pms_mealtype: 'Mealtype',
      pms_persons: 'Occupancy',
      booking_root_rate_id: 'Rate category',
      pms_root_rate_offset: 'Channel offset',
      pms_leading_room_id: 'Base room',
      pms_roomtype_hierarchy: 'Room offsets',
    };

    const changes = [];

    keysToCompare.forEach((key) => {
      const oldValue = originalSettings[key];
      const newValue = currentActiveSettings[key];

      // Check if both values are objects (and not null)
      const isOldObject = typeof oldValue === 'object' && oldValue !== null;
      const isNewObject = typeof newValue === 'object' && newValue !== null;

      // Special handling for pms_roomtype_hierarchy to just return the field name and empty value
      // Done this way so product can count the amount of changes because the full object is too complex to log
      if (key === 'pms_roomtype_hierarchy') {
        if (JSON.stringify(oldValue) !== JSON.stringify(newValue)) {
          changes.push({
            field: keyMap[key],
            oldValue: '',
            newValue: '',
          });
        }
        // Note: this comparison will only go 1 level deep for now
        // Should be good enough for the first version requested by product, so didn't make it recursive to reduce complexity for now
      } else if (isOldObject && isNewObject) {
        // Compare object properties individually
        const allKeys = new Set([
          ...Object.keys(oldValue),
          ...Object.keys(newValue),
        ]);

        allKeys.forEach((objectKey) => {
          if (oldValue[objectKey] !== newValue[objectKey]) {
            changes.push({
              field: `${keyMap[key]} - ${objectKey}`,
              oldValue: oldValue[objectKey],
              newValue: newValue[objectKey],
            });
          }
        });
        // One is object, one is not - they're different
      } else if (isOldObject || isNewObject) {
        changes.push({
          field: keyMap[key],
          oldValue,
          newValue,
        });
        // Both are primitive values, compare directly
      } else if (oldValue !== newValue) {
        changes.push({
          field: keyMap[key],
          oldValue,
          newValue,
        });
      }
    });

    return changes;
  }

  // Get the possible leading room options from the prefill object. The possible leading rooms are dependant on the selected base rate
  // We use Object.keys as these hierarchy objects have dynamic keys representing the room names
  getLeadingRoomsFromHierarchyByBaseRate(baseRateId) {
    return Object.keys(
      this.pmsPrefillOptions.pmsRootRateIdMapping[baseRateId].hierarchy,
    );
  }

  // Helper function to retrieve offset value of a pms room from the roomtype hierarchy
  getOffsetValueByPmsRoomName(pmsRoomName) {
    const pmsLeadingRoom = this.localSettingsCopy.pms_leading_room_id;

    if (pmsRoomName === pmsLeadingRoom) {
      return this.pmsRoomtypeHierarchy[pmsRoomName].offsets[0].value;
    }

    return this.pmsRoomtypeHierarchyLeadingRoomChildren[pmsRoomName].offsets[0]
      .value;
  }

  // In certain cases we need to update the pms_roomtype_hierarchy of our settings.
  // To be able to retrieve the correct hierarchy from the prefill options we need the selected base rate & the selected pms leading room.
  // The object that's returned can be used directly under localSettingsCopy.pms_roomtype_hierarchy.
  getPmsRoomtypeHierarchyObjectByParams(pmsRootRateId, pmsLeadingRoomId) {
    return {
      [pmsLeadingRoomId]:
        this.pmsPrefillOptions.pmsRootRateIdMapping[pmsRootRateId].hierarchy[
          pmsLeadingRoomId
        ],
    };
  }

  // Helper function to return if the PMS room is selected in the roomtype hierarchy
  isPmsRoomSelected(pmsRoomName) {
    const pmsLeadingRoom = this.localSettingsCopy.pms_leading_room_id;

    if (pmsRoomName === pmsLeadingRoom) {
      return this.pmsRoomtypeHierarchy[pmsRoomName].included;
    }

    return this.pmsRoomtypeHierarchyLeadingRoomChildren[pmsRoomName].included;
  }

  // Recursively sort children of each node in the hierarchy
  sortHierarchy(node) {
    const nodeName = Object.keys(node)[0];
    const { children, ...nodeProps } = Object.values(node)[0];

    if (children) {
      const sortedChildren = Object.keys(children)
        .sort()
        .reduce((acc, childName) => {
          acc[childName] = this.sortHierarchy(children[childName]);
          return acc;
        }, {});

      return {
        [nodeName]: {
          ...nodeProps,
          children: sortedChildren,
        },
      };
    }

    return node;
  }

  updateOffsetObject(type, value) {
    let offsetValue = value;

    if (value !== null) {
      if (type.id === PMS_SETTINGS_OFFSET_TYPES.RELATIVE) {
        offsetValue /= 100;
      } else if (type.id === PMS_SETTINGS_OFFSET_TYPES.ABSOLUTE) {
        offsetValue = safeMultiply(value, 100);
      }
    }

    return { type: type.id, value: offsetValue };
  }

  // Helper function to update offsets for each room in the hierarchy in DFS order
  updateOffsetObjectOfAllRooms(type, room) {
    room.offsets = [this.updateOffsetObject(type, room.offsets[0].value)];
    if (room.children) {
      Object.values(room.children).forEach((child) => {
        this.updateOffsetObjectOfAllRooms(type, child);
      });
    }
  }

  @action
  onPmsRoomUnselectionBannerClose() {
    // Store the banner close status in the local storage.
    // To avoid the banner from popping up again if user comes to page again with some room sunselected already.
    this.localStorage.setItem(
      this.localStorageKeyForPmsRoomUnselectionBanner,
      JSON.stringify(false),
    );

    this.canShowPmsRoomUnselectionBanner = false;
  }

  @action
  async onSaveButtonClicked() {
    if (!this.isSaveButtonDisabled) {
      this.usageAnalytics.track('PMS Settings Changed', {
        eventTrigger: 'pms-settings',
        product: 'pricing-manager',
        changedFields: this.compareSettings(
          this.caxxxPmsSettings,
          this.localSettingsCopy,
        ),
      });

      let result = null;

      result = await this.model.saveAndUpdatePmsSettingsTask.perform(
        this.localSettingsCopy,
      );

      if (result.isSuccess) {
        // Update the local settings copy since the save was successful
        this.resetLocalSettingsCopy();

        void this.model.pmsPrefillOptionsTask.perform();

        this.toast.success(this.intl.t('general.changes_saved').toString(), {
          showIcon: true,
        });
      }
    }
  }

  @action
  resetLocalSettingsCopy() {
    this.updateLocalSettingsCopy(
      JSON.parse(JSON.stringify(this.caxxxPmsSettings)),
      false,
    );
    this.pmsRoomIncludedFlags = collectIncludedFlags(
      this.caxxxPmsSettings.pms_roomtype_hierarchy,
    );
  }

  @action
  async toggleCustomerCareAccess() {
    this.localSettingsCopy.customer_care_access_given =
      !this.localSettingsCopy.customer_care_access_given;

    const result = await this.model.saveAndUpdatePmsSettingsTask.perform(
      this.localSettingsCopy,
    );

    if (result.isSuccess) {
      // Update the local settings copy since the save was successful
      this.resetLocalSettingsCopy();
    }
  }

  @action
  updateBaseRate(rate) {
    const newSettingsObject = this.localSettingsCopy;
    newSettingsObject.pms_root_rate_id = rate.id;

    const leadingRoomOptionsForNewBaseRate =
      this.getLeadingRoomsFromHierarchyByBaseRate(rate.id);

    // The leading room options can change when the base rate is updated.
    // We check if the currently selected leading room is available under the newly selected base rate.
    // If not, we default to the first available leading room option for updated base rate.
    if (
      !leadingRoomOptionsForNewBaseRate.includes(
        newSettingsObject.pms_leading_room_id,
      )
    ) {
      newSettingsObject.pms_leading_room_id =
        leadingRoomOptionsForNewBaseRate[0];
    }

    // Update hierarchy on our settings object
    const currentHierarchy = newSettingsObject.pms_roomtype_hierarchy;
    Object.assign(
      this.pmsRoomIncludedFlags,
      collectIncludedFlags(currentHierarchy),
    );
    newSettingsObject.pms_roomtype_hierarchy = carryOverIncludedFlags(
      this.getPmsRoomtypeHierarchyObjectByParams(
        rate.id,
        newSettingsObject.pms_leading_room_id,
      ),
      this.pmsRoomIncludedFlags,
    );

    this.updateLocalSettingsCopy(newSettingsObject);
  }

  @action
  updateChannelDifferenceValue(value) {
    let channelDifferenceValue = value;

    if (
      channelDifferenceValue !== null &&
      this.selectedChannelOffsetType.id === PMS_SETTINGS_OFFSET_TYPES.RELATIVE
    ) {
      channelDifferenceValue /= 100;
    }

    // Update localSettingsCopy
    const newSettingsObject = this.localSettingsCopy;
    newSettingsObject.pms_root_rate_offset.value = channelDifferenceValue;
    this.updateLocalSettingsCopy(newSettingsObject);
  }

  @action
  updateChannelOffsetType(type) {
    // Update local settings copy with selected offset type and adjust offset value accordingly
    const newSettingsObject = this.localSettingsCopy;
    const { source } = newSettingsObject.pms_root_rate_offset;
    newSettingsObject.pms_root_rate_offset = {
      ...this.updateOffsetObject(
        type,
        newSettingsObject.pms_root_rate_offset.value,
      ),
      source,
    };
    this.updateLocalSettingsCopy(newSettingsObject);
  }

  @action
  updateLocalSettingsCopy(
    newSettingsObject,
    hasUserChangedSettingsValue = true,
  ) {
    // localSettingsCopy is tracked, so we use a reassignment here
    this.localSettingsCopy = newSettingsObject;

    this.hasUserChangedSettingsValue = hasUserChangedSettingsValue;

    if (
      this.pricingAssistantPmsSs.isPricingAssistantPMSIntegrated &&
      !this.hasInvalidPmsRoomtypeHierarchyOffsetInputValue &&
      !this.paAutopilotSettings?.enabled
    ) {
      this.launchPmsSettingsSimulateRequestTask.perform();
    }
  }

  @action
  updateMappedChannelRoom(pmsRoom, selectedRoom) {
    const newSettingsObject = this.localSettingsCopy;
    newSettingsObject.pms_id_to_booking_room_name_mapping[pmsRoom] =
      selectedRoom.id;
    this.updateLocalSettingsCopy(newSettingsObject);
  }

  @action
  updatePmsBaseOccupancy(occupancy) {
    const newSettingsObject = this.localSettingsCopy;
    newSettingsObject.pms_persons = occupancy.id;

    // The guest which offset should be set should be the opposite of the base occupancy guest
    // For that we reassign the values in pms_persons_mapping to replace the guest that is already the base guest for the one that will need a offset
    if (this.pmsOccupancyGuestMapping) {
      if (newSettingsObject.pms_persons === 1) {
        newSettingsObject.pms_persons_mapping = {
          [OCCUPANCY_GUESTS.ADULT_2]:
            this.localSettingsCopy.pms_persons_mapping[
              [OCCUPANCY_GUESTS.ADULT_1]
            ],
          [OCCUPANCY_GUESTS.EXTRA_ADULT]:
            this.localSettingsCopy.pms_persons_mapping[
              OCCUPANCY_GUESTS.EXTRA_ADULT
            ],
        };
      } else {
        newSettingsObject.pms_persons_mapping = {
          [OCCUPANCY_GUESTS.ADULT_1]:
            this.localSettingsCopy.pms_persons_mapping[
              [OCCUPANCY_GUESTS.ADULT_2]
            ],
          [OCCUPANCY_GUESTS.EXTRA_ADULT]:
            this.localSettingsCopy.pms_persons_mapping[
              OCCUPANCY_GUESTS.EXTRA_ADULT
            ],
        };
      }
    }
    this.updateLocalSettingsCopy(newSettingsObject);
  }

  @action
  updatePmsExtraGuestOffsetType(type) {
    const newSettingsObject = this.localSettingsCopy;
    newSettingsObject.pms_persons_mapping[OCCUPANCY_GUESTS.EXTRA_ADULT] =
      this.updateOffsetObject(
        type,
        newSettingsObject.pms_persons_mapping[OCCUPANCY_GUESTS.EXTRA_ADULT]
          .value,
      );
    this.updateLocalSettingsCopy(newSettingsObject);
  }

  @action
  updatePmsExtraGuestOffsetValue(value) {
    let pmsExtraGuestOffsetValue = value;

    if (
      pmsExtraGuestOffsetValue !== null &&
      this.selectedPmsExtraGuestOffsetType.id ===
        PMS_SETTINGS_OFFSET_TYPES.RELATIVE
    ) {
      pmsExtraGuestOffsetValue /= 100;
    }

    // Update localSettingsCopy
    const newSettingsObject = this.localSettingsCopy;
    newSettingsObject.pms_persons_mapping[OCCUPANCY_GUESTS.EXTRA_ADULT].value =
      pmsExtraGuestOffsetValue;
    this.updateLocalSettingsCopy(newSettingsObject);
  }

  @action
  updatePmsGuestOffsetType(type) {
    const newSettingsObject = this.localSettingsCopy;
    newSettingsObject.pms_persons_mapping[this.pmsGuest] =
      this.updateOffsetObject(
        type,
        newSettingsObject.pms_persons_mapping[this.pmsGuest].value,
      );
    this.updateLocalSettingsCopy(newSettingsObject);
  }

  @action
  updatePmsGuestOffsetValue(value) {
    let pmsGuestOffsetValue = value;

    if (
      pmsGuestOffsetValue !== null &&
      this.selectedPmsGuestOffsetType.id === PMS_SETTINGS_OFFSET_TYPES.RELATIVE
    ) {
      pmsGuestOffsetValue /= 100;
    }

    // Update localSettingsCopy
    const newSettingsObject = this.localSettingsCopy;
    newSettingsObject.pms_persons_mapping[this.pmsGuest].value =
      pmsGuestOffsetValue;
    this.updateLocalSettingsCopy(newSettingsObject);
  }

  @action
  updatePmsLeadingRoom(room) {
    const newSettingsObject = this.localSettingsCopy;

    newSettingsObject.pms_leading_room_id = room.id;

    // Changing the PMS leading room has an impact on the roomtype hierarchy. Update hierarchy on our settings object
    const currentHierarchy = newSettingsObject.pms_roomtype_hierarchy;
    Object.assign(
      this.pmsRoomIncludedFlags,
      collectIncludedFlags(currentHierarchy),
    );
    newSettingsObject.pms_roomtype_hierarchy = carryOverIncludedFlags(
      this.getPmsRoomtypeHierarchyObjectByParams(
        newSettingsObject.pms_root_rate_id,
        newSettingsObject.pms_leading_room_id,
      ),
      this.pmsRoomIncludedFlags,
    );

    this.updateLocalSettingsCopy(newSettingsObject);
  }

  @action
  updateRoomtypeOffsetsType(type) {
    const newSettingsObject = this.localSettingsCopy;
    const hierarchy = newSettingsObject.pms_roomtype_hierarchy;
    const leadingRoom = Object.values(hierarchy)[0];

    // Update offsets of all the rooms in the hierarchy
    this.updateOffsetObjectOfAllRooms(type, leadingRoom);

    this.updateLocalSettingsCopy(newSettingsObject);
  }

  @action
  updateSelectedChannelMatchingRate(type) {
    // Update local settings copy
    const newSettingsObject = this.localSettingsCopy;
    newSettingsObject.booking_root_rate_id = type;
    this.updateLocalSettingsCopy(newSettingsObject);
  }

  @action
  updateSelectedPmsMealtype(mealtype) {
    const newSettingsObject = this.localSettingsCopy;
    newSettingsObject.pms_mealtype = mealtype;
    this.updateLocalSettingsCopy(newSettingsObject);
  }
}
